import { useTranslation } from 'next-i18next';

import { Box, Flex, Heading, Text, useBreakpointValue, VStack } from '@chakra-ui/react';

import { Image } from '@nocowanie/common-ui/components/atoms/image';

import { AppPromotionLink } from '@app/components/atoms/app-promotion-link';
import { APP_PROMOTION_ASSETS, APP_PROMOTION_SIZES } from '@app/consts';
import { AppPromotionPlatform } from '@app/models';
import { selectors as commonUiSelectors } from '@app/state/slices/common-ui-state.slice';
import { useAppSelector } from '@app/state/state';

export const AppPromotionBanner = () => {
    const { t } = useTranslation('common', { keyPrefix: 'appPromotion' });

    const isMobileBrowser = useAppSelector(commonUiSelectors.selectIsMobileVersion);

    const isMobileRes = useBreakpointValue(
        {
            base: true,
            md: false,
        },
        {
            fallback: isMobileBrowser ? 'base' : 'md',
        },
    );

    const { appShowcaseDesktop, appShowcaseMobile, qrCode } = APP_PROMOTION_ASSETS;

    const {
        showcase: { mobile: showcaseMobile, desktop: showcaseDesktop },
    } = APP_PROMOTION_SIZES;
    const showcaseSize = isMobileRes ? showcaseMobile : showcaseDesktop;

    const sectionProps = {
        as: 'section',
        position: 'relative',
        bg: 'secondary.solid',
        overflowX: 'clip',
    } as const;

    return isMobileRes ? (
        <Box {...sectionProps} pt={{ base: 8, sm: 8 }} my={12}>
            <VStack align="start" px={6}>
                <Heading
                    fontWeight="normal"
                    color="white"
                    size={{ base: 'lg', sm: 'xl' }}
                    dangerouslySetInnerHTML={{ __html: t('titleSm') }}
                />
                <Flex direction="row" gap={{ base: 2, sm: 10 }} mt={5}>
                    <AppPromotionLink platform={AppPromotionPlatform.GooglePlay} isMobile />
                    <AppPromotionLink platform={AppPromotionPlatform.AppStore} isMobile />
                </Flex>
            </VStack>

            <Image
                src={appShowcaseMobile}
                alt=""
                aria-hidden="true"
                width={showcaseSize.width}
                minWidth="450px"
                height="auto"
                ml={3}
                mt={{ base: '-5px', sm: '-25px' }}
                pointerEvents="none"
            />
        </Box>
    ) : (
        <Box
            {...sectionProps}
            width="100%"
            height={{ base: '365px', lg: '410px' }}
            py={{ base: 6, lg: 12 }}
            my={20}
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: { base: '55%', lg: '67%', '2xl': '63%' },
                right: 0,
                bg: 'linkWaterBlue',
                zIndex: 0,
            }}
        >
            <Flex
                maxWidth="containerWidth"
                mx="auto"
                px={{ base: 8, md: 12 }}
                align="center"
                justify="space-between"
                flexDirection={{ base: 'column', md: 'row' }}
                position="relative"
            >
                <VStack
                    align="start"
                    gap={{ base: 5, lg: 6 }}
                    maxW={{ base: '40%', lg: '45%', xl: '55%' }}
                >
                    <Heading
                        fontWeight="normal"
                        color="white"
                        size={{ base: 'sm', lg: 'md', xl: 'xl' }}
                        dangerouslySetInnerHTML={{ __html: t('titleLg') }}
                    />
                    <Text
                        color="white"
                        fontSize={{ base: 'md', xl: 'lg' }}
                        dangerouslySetInnerHTML={{ __html: t('subTitle') }}
                    />

                    <Flex
                        gap={3}
                        align="center"
                        padding="0.25rem 0.75rem .25rem .25rem"
                        borderRadius="md"
                        background="white"
                    >
                        <Image
                            src={qrCode}
                            alt={t('scanQRCode')}
                            htmlWidth="120px"
                            htmlHeight="120px"
                            borderRadius="md"
                        />
                        <Flex direction="column" gap={4}>
                            <AppPromotionLink platform={AppPromotionPlatform.GooglePlay} />
                            <AppPromotionLink platform={AppPromotionPlatform.AppStore} />
                        </Flex>
                    </Flex>
                </VStack>

                <Image
                    src={appShowcaseDesktop}
                    alt=""
                    aria-hidden="true"
                    width={showcaseSize.width}
                    height={showcaseSize.height}
                    position="absolute"
                    top="-160px"
                    right={{ base: '-20%', lg: '-12%', xl: '-9%' }}
                    transform={{ base: 'scale(0.85)', lg: 'unset' }}
                    pointerEvents="none"
                />
            </Flex>
        </Box>
    );
};
